import { useContext } from "react";
import { C } from "./theme";
import { Logo } from "./Logo";
import { KindIcon } from "./kindIcons";
import { NAV_ICONS, NAV_ITEMS } from "./BottomNav";
import { BAR_ICONS, BarButton } from "./TopBar";
import { TopBarContext } from "./TopBarContext";
import { HOME_TILES } from "./homeTiles";
import { visuallyHidden } from "./visuallyHidden";
import { SIDEBAR_WIDTH, SIDEBAR_FOLDED_WIDTH, TOP_BAR_HEIGHT } from "./layout";

// Every page Home's tiles open that the bottom bar's six don't, in Home's order: first what you want and what's coming,
// then the four lists Today's Reminders chooses from, under that name.
const MAIN_IDS = NAV_ITEMS.map(i => i.id);
const REMINDER_IDS = ["mindset", "habits", "gratitude", "temptations"];
const MORE = HOME_TILES.filter(t => !MAIN_IDS.includes(t.to) && !REMINDER_IDS.includes(t.to));
const REMINDERS = HOME_TILES.filter(t => REMINDER_IDS.includes(t.to));

// The sidebar's fold button: a panel with its left side marked.
const FOLD_ICON = (
  <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="16" rx="2" /><line x1="9" y1="4" x2="9" y2="20" />
  </svg>
);

const Divider = () => <div style={{ height: 1, background: C.border, margin: "6px 2px" }} />;

// What a count says to a screen reader, beside the row's name.
const COUNT_WORDS = {
  today: (n) => `${n} ${n === 1 ? "task" : "tasks"} left today`,
  radar: (n) => `${n} ${n === 1 ? "task" : "tasks"} on your radar today`,
  tasks: (n) => `${n} ${n === 1 ? "task" : "tasks"} overdue`,
};

// One row: an icon and a name, and a count when something is waiting. `current` is the page showing. Folded, the row
// is its icon alone (the name is its label and tooltip) with the count as a small badge on it.
function SideItem({ id, icon, label, current = false, color = C.text, count = 0, warn = false, folded = false, onClick }) {
  const countId = count ? `side-count-${id}` : undefined;
  const countColor = warn ? C.red : C.textMid;
  // The description sits outside the button, so it isn't read as part of the button's name.
  return (
    <>
    <button className="side-item" onClick={onClick} aria-current={current ? "page" : undefined} aria-describedby={countId} title={folded ? (count ? `${label}: ${COUNT_WORDS[id](count)}` : label) : undefined}
      style={{ width: "100%", minHeight: 44, display: "flex", alignItems: "center", justifyContent: folded ? "center" : "flex-start", gap: 12, padding: folded ? 0 : "0 12px", border: "none", borderRadius: 10,
        background: current ? C.accentLight : "transparent", color: current ? C.accent : color, fontFamily: "inherit", fontSize: 15, fontWeight: current ? 700 : 600, textAlign: "left", cursor: "pointer" }}>
      <span aria-hidden="true" style={{ lineHeight: 0, flexShrink: 0, position: "relative" }}>
        {icon}
        {folded && count > 0 && (
          <span style={{ position: "absolute", top: -9, right: -13, minWidth: 20, height: 20, padding: "0 5px", boxSizing: "border-box", borderRadius: 10, border: `2px solid ${C.sidebar}`, background: warn ? C.red : C.textMid, color: C.sidebar, fontSize: 11, fontWeight: 800, lineHeight: "16px", textAlign: "center" }}>{count}</span>
        )}
      </span>
      <span style={folded ? visuallyHidden : { flex: 1, minWidth: 0 }}>{label}</span>
      {/* A warning count says what it counts on screen, so its meaning doesn't rest on the colour ("7 overdue"). */}
      {!folded && count > 0 && <span aria-hidden="true" style={{ fontSize: 13, fontWeight: 700, color: countColor, fontVariantNumeric: "tabular-nums" }}>{warn ? `${count} overdue` : count}</span>}
    </button>
    {count > 0 && <span id={countId} style={visuallyHidden}>{COUNT_WORDS[id](count)}</span>}
    </>
  );
}

// The left side of a wide screen (Craig, 4 Nov 2026: "a left side bar on larger screens instead of the bottom navbar",
// like Todoist's). It holds what the bottom bar and the top bar's shared buttons hold on a phone, and every other page
// Home's tiles open, so any page is one tap away. `active` is the screen showing.
// `counts` is what's waiting: { today, radar, tasks } (tasks: overdue, in the warning colour); a page with nothing
// waiting shows no number. `folded` narrows it to its icons; onFold(folded) changes that.
export function Sidebar({ active, onNav, counts = {}, folded = false, onFold }) {
  const actions = useContext(TopBarContext);
  const isActive = (id) => id === active;
  const page = (t) => (
    <SideItem key={t.id} id={t.id} label={t.title} folded={folded} current={isActive(t.to)} onClick={() => onNav(t.to)}
      icon={<KindIcon id={t.id} color={isActive(t.to) ? C.accent : C.textMid} />} />
  );
  const foldButton = <BarButton label={folded ? "Open the sidebar" : "Fold the sidebar"} flat expanded={!folded} onClick={() => onFold(!folded)}>{FOLD_ICON}</BarButton>;
  return (
    <nav aria-label="Main" style={{ width: folded ? SIDEBAR_FOLDED_WIDTH : SIDEBAR_WIDTH, flexShrink: 0, height: "100%", overflowY: "auto", display: "flex", flexDirection: "column", background: C.sidebar, borderRight: `1px solid ${C.border}` }}>
      {/* As tall as the top bar beside it, so the two lines under them meet. */}
      <div style={{ minHeight: TOP_BAR_HEIGHT, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: folded ? "center" : "space-between", padding: folded ? 6 : "6px 4px 6px 18px", boxSizing: "border-box", borderBottom: `1px solid ${C.border}` }}>
        {folded ? foldButton : (
          <>
            <Logo height={34} />
            <div style={{ display: "flex" }}>
              <BarButton label="Nudges" flat current={isActive("nudges")} onClick={actions.onNudges}>{BAR_ICONS.nudges}</BarButton>
              <BarButton label="Settings" flat current={isActive("settings")} onClick={actions.onSettings}>{BAR_ICONS.settings}</BarButton>
              {foldButton}
            </div>
          </>
        )}
      </div>
      <div style={{ padding: folded ? "10px 8px 16px" : "10px 10px 16px", display: "flex", flexDirection: "column" }}>
        <SideItem id="add" label="Add new" folded={folded} color={C.textGreen} onClick={actions.onAdd}
          icon={<span style={{ width: 22, height: 22, borderRadius: "50%", background: C.accent, color: C.onAccent, display: "grid", placeItems: "center" }}>{BAR_ICONS.add}</span>} />
        <SideItem id="search" label="Search" folded={folded} icon={<span style={{ color: C.textMid }}>{BAR_ICONS.search}</span>} onClick={actions.onSearch} />
        <Divider />
        {NAV_ITEMS.map(item => (
          <SideItem key={item.id} id={item.id} label={item.label} folded={folded} current={isActive(item.id)} onClick={() => onNav(item.id)}
            count={counts[item.id] || 0} warn={item.id === "tasks"}
            icon={NAV_ICONS[item.id](isActive(item.id) ? C.accent : C.textMid)} />
        ))}
        <Divider />
        {MORE.map(page)}
        {folded
          ? <Divider />
          : <h2 style={{ margin: "14px 12px 4px", fontSize: 12, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: C.textMid }}>Today's Reminders</h2>}
        {REMINDERS.map(page)}
        {/* Folded, the header has room only for the fold button, so Nudges and Settings join the list. */}
        {folded && (
          <>
            <Divider />
            <SideItem id="nudges" label="Nudges" folded current={isActive("nudges")} onClick={actions.onNudges} icon={<span style={{ color: isActive("nudges") ? C.accent : C.textMid }}>{BAR_ICONS.nudges}</span>} />
            <SideItem id="settings" label="Settings" folded current={isActive("settings")} onClick={actions.onSettings} icon={<span style={{ color: isActive("settings") ? C.accent : C.textMid }}>{BAR_ICONS.settings}</span>} />
          </>
        )}
      </div>
    </nav>
  );
}
